import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { couponsService } from './coupons.service';
import { AppliesTo, DiscountType } from './coupons.types';
import { getErrorMessage } from './coupons.utils';

// Neutralizes the theme's oversized default label/body typography (Inter-Bold, 16px)
// so form fields match the app's normal compact density instead of looking blown up.
// Note: the boldness comes from the theme swapping in the "Inter-Bold" font FILE as
// fontFamily, not from font-weight — so fontFamily has to be overridden, not just weight.
const fieldSx = {
  '& .MuiInputLabel-root': { fontSize: 13, fontWeight: 500, fontFamily: 'Inter-Medium, sans-serif', color: '#667085' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#D9480F' },
  '& .MuiOutlinedInput-input': { fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' },
  '& .MuiFormHelperText-root': { fontSize: 11, margin: '4px 0 0', fontFamily: 'Inter-Medium, sans-serif' },
};

const selectSx = {
  fontWeight: 400,
  fontFamily: 'Inter-Medium, sans-serif',
  '& .MuiSelect-select': { fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' },
};

const selectMenuProps = {
  PaperProps: {
    sx: {
      '& .MuiMenuItem-root': { fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' },
      '& .MuiMenuItem-root.Mui-selected': { backgroundColor: '#EFF8FF', color: '#175CD3' },
      '& .MuiMenuItem-root.Mui-selected:hover': { backgroundColor: '#DCEEFF' },
      '& .MuiMenuItem-root:hover': { backgroundColor: '#F5FAFF' },
    },
  },
};

const menuItemSx = { fontSize: 13, fontWeight: 400, fontFamily: 'Inter-Medium, sans-serif' };

const noTransformSx = { textTransform: 'none' };

const sectionTitleSx = { fontSize: 11, letterSpacing: '0.5px' };

const emptyForm = {
  code: '',
  description: '',
  discountType: DiscountType.Percentage,
  discountValue: '',
  maxDiscount: '',
  appliesTo: AppliesTo.ProcessingFee,
  minLoanAmount: '',
  validFrom: '',
  validUntil: '',
  maxTotalUses: '',
  maxPerUser: '1',
  productIds: '',
  channel: '',
  audience: 'public',
  userId: '',
  isActive: true,
};

export default function CouponFormDrawer({ open, coupon, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (coupon) {
      setForm({
        code: coupon.code || '',
        description: coupon.description || '',
        discountType: coupon.discountType,
        discountValue: String(coupon.discountValue ?? ''),
        maxDiscount: coupon.maxDiscount != null ? String(coupon.maxDiscount) : '',
        appliesTo: coupon.appliesTo,
        minLoanAmount: coupon.minLoanAmount != null && Number(coupon.minLoanAmount) > 0 ? String(coupon.minLoanAmount) : '',
        validFrom: (coupon.validFrom || '').slice(0, 10),
        validUntil: (coupon.validUntil || '').slice(0, 10),
        maxTotalUses: String(coupon.maxTotalUses ?? ''),
        maxPerUser: String(coupon.maxPerUser ?? '1'),
        productIds: (coupon.productIds || []).join(', '),
        channel: coupon.channel || '',
        audience: coupon.userId ? 'user' : 'public',
        userId: coupon.userId || '',
        isActive: coupon.isActive !== false,
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setSaveError(null);
  }, [open, coupon]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const next = {};
    if (!form.code.trim()) next.code = 'Code is required.';
    if (form.description.length > 255) next.description = 'Max 255 characters.';

    const value = parseFloat(form.discountValue);
    if (Number.isNaN(value) || value <= 0 || (form.discountType === DiscountType.Percentage && value > 100)) {
      next.discountValue = 'Enter a valid value.';
    }

    if (form.maxDiscount !== '') {
      const maxDiscount = parseFloat(form.maxDiscount);
      if (Number.isNaN(maxDiscount) || maxDiscount <= 0) next.maxDiscount = 'Must be greater than 0, or leave blank.';
    }

    if (form.minLoanAmount !== '') {
      const minLoanAmount = parseFloat(form.minLoanAmount);
      if (Number.isNaN(minLoanAmount) || minLoanAmount <= 0) next.minLoanAmount = 'Must be greater than 0, or leave blank.';
    }

    if (!form.validFrom) next.validFrom = 'Required.';
    if (!form.validUntil || (form.validFrom && form.validUntil < form.validFrom)) {
      next.validUntil = 'Must be after start date.';
    }

    const maxTotalUses = parseInt(form.maxTotalUses, 10);
    if (Number.isNaN(maxTotalUses) || maxTotalUses < 1) next.maxTotalUses = 'Required.';

    if (form.maxPerUser !== '') {
      const maxPerUser = parseInt(form.maxPerUser, 10);
      if (Number.isNaN(maxPerUser) || maxPerUser < 1) next.maxPerUser = 'Must be at least 1.';
    }

    if (form.audience === 'user' && !form.userId.trim()) {
      next.userId = 'User ID required for single-user coupons.';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    setIsSaving(true);
    setSaveError(null);

    const payload = {
      ...(coupon ? {} : { code: form.code.trim().toUpperCase() }),
      description: form.description.trim() || undefined,
      discountType: form.discountType,
      discountValue: parseFloat(form.discountValue),
      maxDiscount:
        form.discountType === DiscountType.Percentage && form.maxDiscount !== ''
          ? parseFloat(form.maxDiscount)
          : null,
      appliesTo: form.appliesTo,
      minLoanAmount: form.minLoanAmount !== '' ? parseFloat(form.minLoanAmount) : null,
      productIds: form.productIds
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      channel: form.channel || null,
      maxTotalUses: parseInt(form.maxTotalUses, 10),
      maxPerUser: form.maxPerUser !== '' ? parseInt(form.maxPerUser, 10) : 1,
      validFrom: form.validFrom,
      validUntil: form.validUntil,
      userId: form.audience === 'user' ? form.userId.trim() : null,
      isActive: form.isActive,
    };

    const request = coupon
      ? couponsService.updateCouponAdmin(coupon.id, payload)
      : couponsService.createCoupon(payload);

    request
      .then((saved) => {
        onSaved(saved, coupon ? 'updated' : 'created');
        onClose();
      })
      .catch((err) => setSaveError(getErrorMessage(err, 'Could not save coupon.')))
      .finally(() => setIsSaving(false));
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 420, maxWidth: '94vw', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid #e4e8f0',
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 700, fontFamily: 'Inter-Bold, sans-serif' }}>
            {coupon ? 'Edit Coupon' : 'Create Coupon'}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ padding: '18px 20px', overflowY: 'auto', flex: 1 }}>
          {saveError && (
            <Alert severity="error" sx={{ marginBottom: '14px', fontSize: 12.5 }}>
              {saveError}
            </Alert>
          )}

          <TextField
            fullWidth
            size="small"
            label="Coupon Code *"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            error={!!errors.code}
            disabled={!!coupon}
            helperText={errors.code || (coupon ? 'Code cannot be changed after creation.' : 'Max 20 chars')}
            inputProps={{ maxLength: 20 }}
            sx={{ ...fieldSx, marginBottom: '12px' }}
          />

          <TextField
            fullWidth
            size="small"
            label="Description"
            value={form.description}
            onChange={set('description')}
            error={!!errors.description}
            helperText={errors.description || `${form.description.length}/255`}
            inputProps={{ maxLength: 255 }}
            sx={{ ...fieldSx, marginBottom: '14px' }}
          />

          <Typography variant="overline" color="text.secondary" sx={sectionTitleSx}>
            Discount
          </Typography>
          <Box sx={{ display: 'flex', gap: '10px', marginBottom: '12px', marginTop: '6px' }}>
            <Select
              fullWidth
              size="small"
              value={form.discountType}
              onChange={set('discountType')}
              sx={selectSx}
              MenuProps={selectMenuProps}
            >
              <MenuItem value={DiscountType.Percentage} sx={menuItemSx}>Percentage (%)</MenuItem>
              <MenuItem value={DiscountType.Flat} sx={menuItemSx}>Flat amount (₹)</MenuItem>
            </Select>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={form.discountType === DiscountType.Percentage ? 'Percentage (%) *' : 'Amount (₹) *'}
              value={form.discountValue}
              onChange={set('discountValue')}
              error={!!errors.discountValue}
              helperText={errors.discountValue}
              sx={fieldSx}
            />
          </Box>

          {form.discountType === DiscountType.Percentage && (
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Max discount cap (₹)"
              value={form.maxDiscount}
              onChange={set('maxDiscount')}
              error={!!errors.maxDiscount}
              helperText={errors.maxDiscount || 'Optional. Percentage coupons only.'}
              sx={{ ...fieldSx, marginBottom: '12px' }}
            />
          )}

          <Box sx={{ display: 'flex', gap: '10px', marginBottom: '6px' }}>
            <Select
              fullWidth
              size="small"
              value={form.appliesTo}
              onChange={set('appliesTo')}
              sx={selectSx}
              MenuProps={selectMenuProps}
            >
              <MenuItem value={AppliesTo.ProcessingFee} sx={menuItemSx}>Processing fee</MenuItem>
              <MenuItem value={AppliesTo.Emi} sx={menuItemSx}>EMI</MenuItem>
              <MenuItem value={AppliesTo.Foreclosure} sx={menuItemSx}>Foreclosure</MenuItem>
            </Select>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Min loan amount (₹)"
              value={form.minLoanAmount}
              onChange={set('minLoanAmount')}
              error={!!errors.minLoanAmount}
              helperText={errors.minLoanAmount}
              sx={fieldSx}
            />
          </Box>
          {form.appliesTo !== AppliesTo.ProcessingFee && (
            <Alert severity="warning" sx={{ marginBottom: '14px', fontSize: 12.5 }}>
              Backend currently only validates/redeems processing_fee coupons.
            </Alert>
          )}

          <Typography variant="overline" color="text.secondary" sx={{ ...sectionTitleSx, display: 'block', marginTop: '4px' }}>
            Validity &amp; Limits
          </Typography>
          <Box sx={{ display: 'flex', gap: '10px', marginTop: '6px', marginBottom: '12px' }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Valid from *"
              InputLabelProps={{ shrink: true }}
              value={form.validFrom}
              onChange={set('validFrom')}
              error={!!errors.validFrom}
              helperText={errors.validFrom}
              sx={fieldSx}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Valid until *"
              InputLabelProps={{ shrink: true }}
              value={form.validUntil}
              onChange={set('validUntil')}
              error={!!errors.validUntil}
              helperText={errors.validUntil}
              sx={fieldSx}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Total usage limit *"
              value={form.maxTotalUses}
              onChange={set('maxTotalUses')}
              error={!!errors.maxTotalUses}
              helperText={errors.maxTotalUses}
              sx={fieldSx}
            />
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Per-user limit"
              value={form.maxPerUser}
              onChange={set('maxPerUser')}
              error={!!errors.maxPerUser}
              helperText={errors.maxPerUser}
              sx={fieldSx}
            />
          </Box>

          <Typography variant="overline" color="text.secondary" sx={sectionTitleSx}>
            Targeting &amp; Restrictions
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Product IDs"
            value={form.productIds}
            onChange={set('productIds')}
            helperText="Comma separated, blank = all products."
            sx={{ ...fieldSx, marginTop: '6px', marginBottom: '12px' }}
          />
          <Box sx={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={form.channel}
              onChange={set('channel')}
              sx={selectSx}
              MenuProps={selectMenuProps}
            >
              <MenuItem value="" sx={menuItemSx}>All channels</MenuItem>
              <MenuItem value="APP_ANDROID" sx={menuItemSx}>App - Android</MenuItem>
              <MenuItem value="APP_IOS" sx={menuItemSx}>App - iOS</MenuItem>
              <MenuItem value="WEB" sx={menuItemSx}>Web</MenuItem>
            </Select>
            <Select
              fullWidth
              size="small"
              value={form.audience}
              onChange={set('audience')}
              sx={selectSx}
              MenuProps={selectMenuProps}
            >
              <MenuItem value="public" sx={menuItemSx}>Public (all users)</MenuItem>
              <MenuItem value="user" sx={menuItemSx}>Single user (userId)</MenuItem>
            </Select>
          </Box>
          {form.audience === 'user' && (
            <TextField
              fullWidth
              size="small"
              label="User ID"
              value={form.userId}
              onChange={set('userId')}
              error={!!errors.userId}
              helperText={errors.userId}
              sx={{ ...fieldSx, marginBottom: '12px' }}
            />
          )}

          <FormControlLabel
            sx={{ marginTop: '6px', '& .MuiFormControlLabel-label': { fontSize: 13, fontFamily: 'Inter-Medium, sans-serif' } }}
            control={
              <Switch
                size="small"
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
              />
            }
            label="Active"
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'flex-end',
            padding: '14px 20px',
            borderTop: '1px solid #e4e8f0',
          }}
        >
          <Button size="small" onClick={onClose} disabled={isSaving} sx={{ ...noTransformSx, fontSize: 12.5 }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            sx={{ ...noTransformSx, color: '#fff', fontSize: 12.5 }}
          >
            {isSaving ? 'Saving...' : 'Save Coupon'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
